import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import BlurredRadar from "@/components/BlurredRadar";

export default function PreResult() {
  const [bookingUrl, setBookingUrl] = useState("");
  const [scores, setScores] = useState(null);

  useEffect(() => {
    api.get("/booking-url").then((r) => setBookingUrl(r.data.booking_url || ""));
    try {
      const s = sessionStorage.getItem("li_diag_scores");
      if (s) setScores(JSON.parse(s));
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <div className="relative min-h-screen bg-sand-50" data-testid="preresult-page">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sand-100/40 to-sand-50" />

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
        >
          <p className="text-xs uppercase tracking-[0.32em] text-sand-500 flex items-center gap-3">
            <span className="h-px w-8 bg-sand-400" />
            Étape finale
            <span className="text-sand-400">✦</span>
          </p>
          <h1
            className="mt-5 font-display text-4xl md:text-6xl leading-[1.05] text-ink"
            data-testid="preresult-title"
          >
            Votre diagnostic est terminé.
          </h1>
          <p className="mt-4 italic text-xl md:text-2xl text-sand-500">
            Merci pour ce que vous venez de déposer.
          </p>

          <div className="mt-10 space-y-5 max-w-2xl text-ink-soft leading-relaxed">
            <p>
              Ce que vous avez répondu dessine quelque chose.
              Pas un verdict, plutôt un éclairage sur des dynamiques
              qui méritent d'être regardées avec attention.
            </p>
            <p>
              Ce diagnostic ouvre une porte. Ce qu'il y a derrière
              prend tout son sens quand on en parle, à voix haute,
              avec quelqu'un qui sait écouter ce qui n'est pas dit.
            </p>
          </div>
        </motion.div>

        {/* Blurred radar, teaser */}
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.9 }}
          className="mt-14 relative rounded-3xl border border-sand-200 bg-gradient-to-br from-white via-sand-50 to-sand-100/50 p-6 md:p-10 shadow-soft overflow-hidden"
          data-testid="teaser-radar"
        >
          {/* decorative sparkles */}
          <div className="absolute top-6 right-8 text-sand-300 text-2xl select-none" aria-hidden>✦</div>
          <div className="absolute bottom-10 left-10 text-sand-300/70 text-xl select-none" aria-hidden>✦</div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="relative">
              <BlurredRadar scores={scores || {}} />
              {/* center reveal badge */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-white/90 backdrop-blur-sm rounded-full px-5 py-3 shadow-soft border border-sand-200 flex items-center gap-2">
                  <span className="text-sand-500 text-base" aria-hidden>✦</span>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-sand-500 font-medium">
                    Révélation lors du debrief
                  </span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-sand-500">Votre carte intérieure</p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl text-ink leading-tight">
                Quatre dynamiques se dessinent.
              </h2>
              <p className="mt-4 text-ink-soft leading-relaxed">
                Vos réponses tracent un paysage qui n'appartient qu'à vous.
                L'une de ces dynamiques pèse plus que les autres aujourd'hui,
                c'est elle qui mérite d'être nommée et accueillie.
              </p>
              <p className="mt-4 text-ink leading-relaxed">
                Le détail vous sera
                <span className="italic text-sand-500"> révélé lors du debrief </span>
                avec Cindy.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Why reveal during the debrief, explain the soft approach */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.8 }}
          className="mt-10 rounded-3xl border border-sand-200 bg-white/70 backdrop-blur-sm p-6 md:p-9"
          data-testid="why-debrief"
        >
          <p className="text-xs uppercase tracking-[0.32em] text-sand-500 flex items-center gap-3">
            <span className="h-px w-8 bg-sand-400" />
            Pourquoi ce choix
            <span className="text-sand-400">✦</span>
          </p>
          <h3 className="mt-4 font-display text-2xl md:text-3xl text-ink leading-snug max-w-2xl">
            Un résultat ne se lit pas tout seul.
            <span className="italic text-sand-500"> Surtout pas celui-ci.</span>
          </h3>
          <div className="mt-6 grid md:grid-cols-3 gap-6 text-sm md:text-base text-ink-soft leading-relaxed">
            <div>
              <p className="font-display text-sand-500 text-lg mb-2">Pas d'étiquette froide</p>
              <p>
                Un profil affiché brut, sans contexte, peut figer ce que vous êtes
                en une catégorie. Or vous n'êtes pas une case, vous êtes une histoire.
              </p>
            </div>
            <div>
              <p className="font-display text-sand-500 text-lg mb-2">Pas de mauvaise lecture</p>
              <p>
                Lu seule à l'écran, une analyse peut être mal interprétée, sous-estimée,
                ou au contraire vécue trop durement. La voix d'une personne change tout.
              </p>
            </div>
            <div>
              <p className="font-display text-sand-500 text-lg mb-2">L'écoute fait le travail</p>
              <p>
                Mettre des mots sur ce que vous traversez, à voix haute, avec quelqu'un
                qui sait écouter, c'est souvent là que la prise de conscience devient apaisante.
              </p>
            </div>
          </div>
          <p className="mt-6 italic text-ink-soft max-w-2xl leading-relaxed">
            C'est pour cela que Cindy garde la lecture pour le moment du debrief.
            Pas par mystère, par respect de ce qui se joue chez vous.
          </p>
        </motion.section>

        {/* What the visio brings */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mt-10 max-w-3xl"
        >
          <p className="text-ink leading-relaxed">
            Pendant l'échange, Cindy vous aide à&nbsp;:
          </p>
          <ul className="mt-3 space-y-2 text-ink-soft">
            <li className="flex gap-3"><span className="text-sand-500">✦</span> reconnaître ce qui se joue, avec ses mots à elle</li>
            <li className="flex gap-3"><span className="text-sand-500">✦</span> nommer ce qui vous épuise sans que vous le voyiez</li>
            <li className="flex gap-3"><span className="text-sand-500">✦</span> commencer à entrevoir ce qui peut s'alléger</li>
          </ul>
          <div className="mt-6 flex items-center gap-2 text-sm text-ink-soft">
            <span className="h-px w-8 bg-sand-400" />
            <span className="italic">Réservez votre moment ci-dessous</span>
          </div>
        </motion.section>

        {/* Google Calendar embed */}
        <motion.section
          id="calendar-embed"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.9 }}
          className="mt-12 rounded-3xl border border-sand-200 bg-white p-4 md:p-6 shadow-soft overflow-hidden"
          data-testid="booking-section"
        >
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 px-3 pt-3 pb-5">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-sand-500">
                Calendrier de Cindy
              </p>
              <h2 className="font-display text-3xl md:text-5xl text-ink mt-2 leading-tight">
                Choisissez votre moment
              </h2>
              <p className="mt-2 text-sm md:text-base text-ink-soft max-w-xl">
                Environ 30 minutes, en visio. Sans engagement, ni reconduite automatique, juste un échange.
              </p>
            </div>
            {bookingUrl && (
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-sand-600 hover:text-sand-700 underline-offset-4 hover:underline whitespace-nowrap"
                data-testid="open-booking-new-tab"
              >
                Ouvrir dans un nouvel onglet ↗
              </a>
            )}
          </div>
          <div className="relative w-full" style={{ height: "820px" }}>
            {bookingUrl ? (
              <iframe
                src={bookingUrl}
                title="Réservation Google Calendar"
                className="absolute inset-0 w-full h-full rounded-2xl border border-sand-100 bg-white"
                frameBorder="0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="calendar-iframe"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-ink-soft animate-breath">
                Chargement du calendrier
              </div>
            )}
          </div>
          <p className="mt-4 text-xs text-ink-muted text-center italic">
            Si le calendrier ne s'affiche pas correctement,{" "}
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-sand-600"
            >
              ouvrez-le dans un nouvel onglet
            </a>.
          </p>
        </motion.section>

        <p className="mt-10 text-center text-sm text-ink-muted italic max-w-xl mx-auto leading-relaxed">
          Le détail de votre profil est gardé pour l'échange,
          pour qu'il vous soit transmis avec les mots justes, pas seulement à l'écran.
        </p>
      </main>
    </div>
  );
}
